import {
  Delete, DeleteForever, Replay, Save,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { TooltipIconButton } from 'shared/components/abstract/interactables';
import { TextEditor, withDataValidator } from 'shared/components/data-display/editors';
import { dateString } from 'shared/utils';
import { maxNoteLength } from 'shared/components/data-display/helper';

/**
 * @param {Object} props - Component props
 * @param {*} props.note - Note object to edit or create
 * @param {string} [props.size='small'] - Size of the input field
 * @param {Function} [props.onSaveNote] - Callback when note is saved
 * @param {Function} props.onDeleteNote - Callback when note is deleted
 * @param {boolean} [props.savable=true] - Whether the note can be saved
 * @param {Function} [props.onChange] - Callback when note value changes
 * @param {Function} [props.setDirty] - Callback to set dirty state
 * @param {boolean} [props.deleteOnly=false] - Whether only delete action is allowed
 * @param {boolean} [props.disableDelete=false] - Whether delete is disabled
 * @param {boolean} [props.creator=false] - Whether this is in creator mode
 * @returns {JSX.Element}
 */
export default function NoteInput({
  note,
  size = 'small',
  onSaveNote = () => {
  },
  onDeleteNote,
  savable = true,
  onChange = () => {
  },
  setDirty = () => {
  },
  deleteOnly = false,
  disableDelete = false,
  creator = false,
}) {
  const [value, setValue] = useState(note.value);
  const [valid, setValid] = useState(true);

  const labelRef = useRef(note.note_type?.name);

  const overLength = value?.length > maxNoteLength;
  const errorMessage = overLength
    ? (
      <Typography
        sx={{ display: 'block' }}
        variant="caption"
        component="span"
        align="right"
      >
        {`Exceeded max length. ${value?.length || 0}/${maxNoteLength} Characters`}
      </Typography>
    )
    : `Invalid format for ${labelRef.current}`;

  const handleChange = useCallback((v) => {
    setValue(v);
    onChange(v);
  }, [onChange]);

  function validate(valueToTest) {
    if (overLength) {
      return !valueToTest;
    }
    return !!valueToTest;
  }

  useEffect(() => {
    // Sync local state when the note prop's value changes. Use setValue directly
    // rather than handleChange so we don't push the prop value back up through
    // onChange (which dispatches to the prop's source and re-triggers this effect).
    setValue(note.value);
  }, [note.value]);

  useEffect(() => {
    // Ensures that if the note name is cleared by the selector,
    // that the note input value is set to an empty string
    if (!note.note_type?.name) {
      handleChange('');
      return;
    }
    if (labelRef.current !== note.note_type?.name) {
      labelRef.current = note.note_type?.name;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.note_type?.name]);

  useEffect(() => setDirty(note.value !== value), [note.value, value, setDirty]);

  const handleRevert = () => {
    setValue(note.value);
  };

  const handleSave = () => {
    setValue(note.value);
    onSaveNote(value);
  };

  const updatedDateText = () => {
    if (value === note.value) {
      if (note?.updated_at > note?.created_at) {
        return (
          <Typography
            sx={{ display: 'block' }}
            variant="caption"
            component="span"
            align="right"
          >
            {`Updated On: ${dateString(note?.updated_at)}`}
          </Typography>
        );
      }
      if (note?.created_at) {
        return (
          <Typography
            sx={{ display: 'block' }}
            variant="caption"
            component="span"
            align="right"
          >
            {`Created On: ${dateString(note?.created_at)}`}
          </Typography>
        );
      }
    }
    return '';
  };

  const counterText = () => (
    <Typography
      sx={{ display: 'block' }}
      variant="caption"
      component="span"
      align="right"
    >
      {`${value?.length || '0'}/${maxNoteLength} Characters`}
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextEditor
          disabled={deleteOnly || !note.note_type}
          setValue={handleChange}
          size={size}
          fullWidth
          placeholder={!note.note_type ? '...label selection required' : ''}
          required
          label={note?.note_type?.name || ''}
          value={value}
          helperText={(value !== note?.value || creator) ? counterText() : updatedDateText()}
          validator={withDataValidator({
            validatorFunc: validate,
            helperText: errorMessage,
            setValid,
          })}
          slotProps={{
            input: {
              sx: {
                backgroundColor: 'inputBackground',
                lineHeight: '1.75rem',
              },
            },
          }}
          multiline
        />
      </Box>
      {!creator && value !== note?.value
        && (
          <Box sx={{ marginTop: (theme) => theme.spacing(0.75) }}>
            <TooltipIconButton
              message="Reset"
              placement="top"
              onClick={handleRevert}
              icon={Replay}
            />
            <TooltipIconButton
              message="Save"
              disabled={!savable || !value?.length > 0 || !valid}
              placement="top"
              onClick={() => handleSave()}
              icon={Save}
            />
          </Box>
        )}
      {(value === note?.value || creator) && (
        <Box sx={{ marginTop: (theme) => theme.spacing(0.75) }}>
          <TooltipIconButton
            disabled={disableDelete}
            message={`Delete ${note.note_type ? note?.note_type?.name : 'pending'} note`}
            placement="left"
            icon={!note.note_type && disableDelete ? DeleteForever : Delete}
            onClick={() => {
              onDeleteNote(note.id);
            }}
          />
        </Box>
      )}
    </Box>
  );
}
